import { useEffect, useRef, useState } from 'react';

/**
 * GoldSplash — the pre-launch hero: the "Gilding" wordmark rendered as real
 * gold in WebGL and gilded on page load.
 *
 * Technique (verified visually via headless captures before shipping):
 *   - The wordmark (Fraunces 900) is converted to an exact signed distance
 *     field (Felzenszwalb EDT) at float precision — crisp at any scale.
 *   - A hand-painted MatCap (studio softboxes on black) lights the metal;
 *     a circular-arc bevel profile gives cast-and-polished letter shoulders.
 *   - Entrance: sheets of gold leaf sweep on diagonally, each catching its
 *     own light and flashing as it is pressed flat; seams glint, then fade.
 *   - Interaction: dragging across the mark BURNISHES it — a mirror-polish
 *     streak with a warm gleam that trails and fades. Click = polish pop.
 *
 * Three.js is imported dynamically so this chunk only loads on the splash —
 * the drop page and main app pay nothing. On WebGL failure (or while
 * loading) a static gradient wordmark renders instead, so the page never
 * looks broken.
 */

interface GoldHandle {
  dispose: () => void;
  regild: () => void;
}

export function GoldSplash({
  className,
  onSettled,
  bindRegild,
}: {
  className?: string;
  onSettled?: () => void;
  /** Receives a callback that replays the gilding entrance. */
  bindRegild?: (regild: () => void) => void;
}) {
  const hostRef = useRef<HTMLDivElement>(null);
  const handleRef = useRef<GoldHandle | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let disposed = false;
    const host = hostRef.current;
    if (!host) return;
    void (async () => {
      try {
        const handle = await initGold(host, onSettled);
        if (disposed) {
          handle.dispose();
          return;
        }
        handleRef.current = handle;
        bindRegild?.(handle.regild);
      } catch (e) {
        console.warn('GoldSplash: WebGL init failed, using static mark', e);
        setFailed(true);
        onSettled?.();
      }
    })();
    return () => {
      disposed = true;
      handleRef.current?.dispose();
      handleRef.current = null;
    };
    // Mount-once by design: the WebGL world is built a single time and torn
    // down on unmount; onSettled/bindRegild are stable callbacks from the page.
  }, []);

  return (
    <div ref={hostRef} className={className} aria-hidden="true">
      {failed && (
        <div className="flex h-full w-full items-center justify-center">
          <span
            className="font-display text-[clamp(3.5rem,12vw,9rem)] font-black tracking-tight"
            style={{
              backgroundImage:
                'linear-gradient(175deg,#8a5d1d 0%,#e9c873 45%,#fff3ce 52%,#c2922f 60%,#5d3f10 100%)',
              WebkitBackgroundClip: 'text',
              backgroundClip: 'text',
              color: 'transparent',
            }}
          >
            Gilding
          </span>
        </div>
      )}
    </div>
  );
}

/* ====================================================================== */

async function initGold(host: HTMLElement, onSettled?: () => void): Promise<GoldHandle> {
  const THREE = await import('three');
  const { EffectComposer } = await import('three/addons/postprocessing/EffectComposer.js');
  const { RenderPass } = await import('three/addons/postprocessing/RenderPass.js');
  const { UnrealBloomPass } = await import('three/addons/postprocessing/UnrealBloomPass.js');

  await document.fonts.load('900 352px "Fraunces"');
  await document.fonts.ready;

  const reduceMotion =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const isMobile = Math.min(innerWidth, innerHeight) < 700;
  const coarse =
    typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches;

  /* ---------- exact EDT (Felzenszwalb) → float SDF of the wordmark ---------- */
  const TW = isMobile ? 1280 : 2048;
  const THh = isMobile ? 400 : 640;
  const SPREAD = TW * 0.0273;
  const BIG = 1e20;
  function edt1d(
    f: Float64Array,
    d: Float64Array,
    v: Int32Array,
    z: Float64Array,
    n: number,
  ): void {
    let k = 0;
    v[0] = 0;
    z[0] = -BIG;
    z[1] = BIG;
    for (let q = 1; q < n; q++) {
      let s = (f[q]! + q * q - (f[v[k]!]! + v[k]! * v[k]!)) / (2 * q - 2 * v[k]!);
      while (s <= z[k]!) {
        k--;
        s = (f[q]! + q * q - (f[v[k]!]! + v[k]! * v[k]!)) / (2 * q - 2 * v[k]!);
      }
      k++;
      v[k] = q;
      z[k] = s;
      z[k + 1] = BIG;
    }
    k = 0;
    for (let q = 0; q < n; q++) {
      while (z[k + 1]! < q) k++;
      d[q] = (q - v[k]!) * (q - v[k]!) + f[v[k]!]!;
    }
  }
  function edt2d(grid: Float64Array, w: number, h: number): void {
    const m = Math.max(w, h);
    const f = new Float64Array(m);
    const d = new Float64Array(m);
    const v = new Int32Array(m);
    const z = new Float64Array(m + 1);
    for (let x = 0; x < w; x++) {
      for (let y = 0; y < h; y++) f[y] = grid[y * w + x]!;
      edt1d(f, d, v, z, h);
      for (let y = 0; y < h; y++) grid[y * w + x] = d[y]!;
    }
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) f[x] = grid[y * w + x]!;
      edt1d(f, d, v, z, w);
      for (let x = 0; x < w; x++) grid[y * w + x] = d[x]!;
    }
  }
  function buildSDF(): InstanceType<typeof THREE.DataTexture> {
    const c = document.createElement('canvas');
    c.width = TW;
    c.height = THh;
    const g = c.getContext('2d')!;
    g.fillStyle = '#fff';
    g.textAlign = 'center';
    g.textBaseline = 'middle';
    g.font = `900 ${Math.round(TW * 0.172)}px "Fraunces", serif`;
    g.fillText('Gilding', TW / 2, THh / 2 + TW * 0.006);
    const img = g.getImageData(0, 0, TW, THh).data;
    const n = TW * THh;
    const inside = new Float64Array(n);
    const outside = new Float64Array(n);
    for (let i = 0; i < n; i++) {
      const a = img[i * 4 + 3]! > 127;
      inside[i] = a ? BIG : 0;
      outside[i] = a ? 0 : BIG;
    }
    edt2d(inside, TW, THh);
    edt2d(outside, TW, THh);
    const data = new Float32Array(n);
    for (let i = 0; i < n; i++)
      data[i] = (Math.sqrt(outside[i]!) - Math.sqrt(inside[i]!)) / SPREAD;
    const tex = new THREE.DataTexture(data, TW, THh, THREE.RedFormat, THREE.FloatType);
    tex.minFilter = tex.magFilter = THREE.LinearFilter;
    tex.flipY = true;
    tex.needsUpdate = true;
    return tex;
  }

  /* ---------- hand-painted gold matcap ---------- */
  function paintMatcap(): InstanceType<typeof THREE.CanvasTexture> {
    const S = 512;
    const c = document.createElement('canvas');
    c.width = c.height = S;
    const g = c.getContext('2d')!;
    const cx = S / 2,
      cy = S / 2,
      R = S / 2;
    const base = g.createLinearGradient(0, 0, 0, S);
    base.addColorStop(0, '#7d521a');
    base.addColorStop(0.4, '#c2922f');
    base.addColorStop(0.6, '#5d3f10');
    base.addColorStop(1, '#1a0f02');
    g.fillStyle = base;
    g.beginPath();
    g.arc(cx, cy, R, 0, 7);
    g.fill();
    const lg = (x: number, y: number, r: number, stops: [number, string][]) => {
      const G = g.createRadialGradient(x, y, 0, x, y, r);
      stops.forEach((s) => G.addColorStop(s[0], s[1]));
      g.fillStyle = G;
      g.beginPath();
      g.arc(cx, cy, R, 0, 7);
      g.fill();
    };
    lg(S * 0.34, S * 0.26, S * 0.55, [
      [0, 'rgba(255,250,232,.97)'],
      [0.26, 'rgba(255,226,160,.5)'],
      [1, 'rgba(255,226,160,0)'],
    ]);
    lg(S * 0.36, S * 0.24, S * 0.1, [
      [0, 'rgba(255,255,255,1)'],
      [1, 'rgba(255,255,255,0)'],
    ]);
    g.save();
    g.globalCompositeOperation = 'lighter';
    const G2 = g.createLinearGradient(S * 0.78, 0, S, 0);
    G2.addColorStop(0, 'rgba(120,140,170,0)');
    G2.addColorStop(0.7, 'rgba(190,210,235,.36)');
    G2.addColorStop(1, 'rgba(235,242,255,.66)');
    g.fillStyle = G2;
    g.beginPath();
    g.arc(cx, cy, R, -0.9, 0.9);
    g.lineTo(cx, cy);
    g.fill();
    g.restore();
    lg(S * 0.5, S * 1.02, S * 0.5, [
      [0, 'rgba(255,150,50,.5)'],
      [1, 'rgba(255,150,50,0)'],
    ]);
    g.save();
    g.globalCompositeOperation = 'multiply';
    const G3 = g.createLinearGradient(0, S * 0.5, 0, S * 0.82);
    G3.addColorStop(0, 'rgba(255,255,255,1)');
    G3.addColorStop(0.45, 'rgba(60,38,12,1)');
    G3.addColorStop(1, 'rgba(255,255,255,1)');
    g.fillStyle = G3;
    g.beginPath();
    g.arc(cx, cy, R, 0, 7);
    g.fill();
    g.restore();
    const c2 = document.createElement('canvas');
    c2.width = c2.height = S;
    const g2 = c2.getContext('2d')!;
    g2.filter = 'blur(2.5px)';
    g2.drawImage(c, 0, 0);
    const t = new THREE.CanvasTexture(c2);
    t.colorSpace = THREE.SRGBColorSpace;
    t.minFilter = THREE.LinearMipmapLinearFilter;
    t.generateMipmaps = true;
    return t;
  }

  /* ---------- renderer & scene ---------- */
  const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
  renderer.setPixelRatio(Math.min(isMobile ? 1.5 : 2, devicePixelRatio));
  renderer.setSize(host.clientWidth, host.clientHeight);
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.05;
  renderer.domElement.style.position = 'absolute';
  renderer.domElement.style.inset = '0';
  // Keep pointer/touch events flowing during a drag. Without touch-action:none
  // iOS hands the gesture off to scroll/overscroll after a few pixels and stops
  // firing pointermove — so a finger drag burnishes the gold exactly like a
  // desktop cursor does.
  renderer.domElement.style.touchAction = 'none';
  host.style.touchAction = 'none';
  host.appendChild(renderer.domElement);

  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(
    30,
    host.clientWidth / host.clientHeight,
    0.1,
    50,
  );
  camera.position.set(0, 0, 10);

  const PW = 11.2;
  const PH = (PW * THh) / TW;
  const ASPECT = PW / PH;
  const TRAIL = 12;
  const uniforms = {
    uSDF: { value: buildSDF() },
    uMat: { value: paintMatcap() },
    uTime: { value: 0 },
    uApply: { value: 0 },
    uAspect: { value: ASPECT },
    uTrail: {
      value: Array.from({ length: TRAIL }, () => new THREE.Vector4(0, 0, 99, 0)),
    },
    uMirror: { value: 0 },
  };

  const frag = /* glsl */ `
precision highp float;
varying vec2 vUv;
uniform sampler2D uSDF,uMat;
uniform float uTime,uApply,uAspect,uMirror;
uniform vec4 uTrail[${TRAIL}];
float hash(vec2 p){return fract(sin(dot(p,vec2(127.1,311.7)))*43758.5453);}
vec2 hash2(vec2 p){return vec2(hash(p),hash(p+19.7));}
vec2 grad(vec2 i){float h=hash(i)*6.2831853;return vec2(cos(h),sin(h));}
float gnoise(vec2 p){
  vec2 i=floor(p),f=fract(p);
  vec2 u=f*f*f*(f*(f*6.-15.)+10.);
  return mix(mix(dot(grad(i),f),dot(grad(i+vec2(1,0)),f-vec2(1,0)),u.x),
             mix(dot(grad(i+vec2(0,1)),f-vec2(0,1)),dot(grad(i+vec2(1,1)),f-vec2(1,1)),u.x),u.y);
}
const mat2 ROT=mat2(.8,-.6,.6,.8);
float fbm(vec2 p){float v=0.,a=.5;for(int i=0;i<5;i++){v+=a*gnoise(p);p=ROT*p*2.02;a*=.5;}return v;}
float sdf(vec2 uv){return texture2D(uSDF,uv).r;}
const float LEAF=0.34;
const mat2 LR=mat2(0.993,-0.119,0.119,0.993);
vec2 leafCell(vec2 p){return floor((LR*p)/LEAF);}
vec2 leafLocal(vec2 p){return fract((LR*p)/LEAF);}
float leafDelay(vec2 cell){
  return (cell.x*LEAF/uAspect)*0.50 + (1.0-cell.y*LEAF)*0.10 + hash(cell)*0.28;
}
float leafSettle(vec2 cell){
  float d0=leafDelay(cell);
  return smoothstep(0.0,1.0,clamp((uApply-d0)/0.22,0.,1.));
}
float leafVis(){return smoothstep(1.55,1.12,uApply);}
float burnish(vec2 p){
  float b=0.;
  for(int i=0;i<${TRAIL};i++){
    vec2 tp=vec2(uTrail[i].x*uAspect,uTrail[i].y);
    float age=uTrail[i].z,amp=uTrail[i].w;
    float r2=dot(p-tp,p-tp);
    b+=amp*exp(-r2*55.0)*exp(-age*2.6);
  }
  return clamp(b,0.,1.2);
}
float heightAt(vec2 uv,vec2 p,float d){
  float x=clamp(-d/0.34,0.,1.);
  float plate=sqrt(x*(2.0-x));
  vec2 cell=leafCell(p);
  float lv=leafVis();
  float leafH=(hash(cell+7.3)-0.5)*0.016*lv;
  float foil=fbm(p*6.0)*0.012 + gnoise(p*40.0)*0.0030;
  float face=smoothstep(0.78,0.985,plate);
  return plate + (leafH+foil)*face;
}
void main(){
  vec2 p=vec2(vUv.x*uAspect,vUv.y);
  float d=sdf(vUv);
  vec2 cell=leafCell(p);
  float settle=leafSettle(cell);
  float aa=max(fwidth(d),0.004)*1.4;
  float a=smoothstep(aa,-aa,d)*settle;
  vec2 e=vec2(1.6/${TW}.0,1.6/${THh}.0);
  float hx=heightAt(vUv+vec2(e.x,0.),p+vec2(e.x*uAspect,0.),sdf(vUv+vec2(e.x,0.)))
          -heightAt(vUv-vec2(e.x,0.),p-vec2(e.x*uAspect,0.),sdf(vUv-vec2(e.x,0.)));
  float hy=heightAt(vUv+vec2(0.,e.y),p+vec2(0.,e.y),sdf(vUv+vec2(0.,e.y)))
          -heightAt(vUv-vec2(0.,e.y),p-vec2(0.,e.y),sdf(vUv-vec2(0.,e.y)));
  vec3 n=normalize(vec3(-hx*8.0,-hy*8.0,1.0));
  vec2 tiltJ=(hash2(cell)-0.5)*1.4*(1.0-settle);
  n=normalize(vec3(n.xy+tiltJ,n.z));
  float B=burnish(p);
  n=normalize(mix(n,vec3(0.,0.,1.),clamp(B*0.55,0.,0.6)));
  vec3 mc=texture2D(uMat,n.xy*0.5+0.5).rgb;
  vec3 tint=mix(vec3(.55,.36,.12),vec3(1.10,.96,.72),clamp(n.y*.5+.62,0.,1.));
  vec3 col=mc*tint*1.18;
  col+=pow(mc.r,6.0)*vec3(1.,.95,.8)*.6;
  float flash=settle*(1.0-settle)*4.0;
  col+=vec3(1.0,.9,.6)*flash*.9;
  vec2 lf=leafLocal(p);
  float seam=1.0-smoothstep(0.0,0.05,min(min(lf.x,1.-lf.x),min(lf.y,1.-lf.y)));
  float lv2=leafVis();
  col+=vec3(1.,.85,.5)*seam*(1.0-settle)*.7;
  col*=1.0-seam*mix(0.012,0.05,lv2);
  col+=vec3(1.,.93,.72)*B*.32;
  col*=1.0+B*.18;
  float aura=exp(max(d,0.)*-6.0)*.10*settle;
  if(uMirror>.5){col*=.36;a*=smoothstep(.02,.8,vUv.y)*.5;aura*=.15;}
  gl_FragColor=vec4(col*a+vec3(1.,.78,.45)*aura*(1.-a),max(a,aura));
}`;
  const vert =
    'varying vec2 vUv;void main(){vUv=uv;gl_Position=projectionMatrix*modelViewMatrix*vec4(position,1.);}';

  const group = new THREE.Group();
  scene.add(group);
  const mesh = new THREE.Mesh(
    new THREE.PlaneGeometry(PW, PH),
    new THREE.ShaderMaterial({
      uniforms,
      vertexShader: vert,
      fragmentShader: frag,
      transparent: true,
    }),
  );
  group.add(mesh);
  const mU: Record<string, unknown> = {};
  (['uSDF', 'uMat', 'uTime', 'uApply', 'uAspect', 'uTrail'] as const).forEach(
    (k) => (mU[k] = uniforms[k]),
  );
  mU.uMirror = { value: 1 };
  const mirror = new THREE.Mesh(
    new THREE.PlaneGeometry(PW, PH),
    new THREE.ShaderMaterial({
      uniforms: mU as typeof uniforms,
      vertexShader: vert,
      fragmentShader: frag,
      transparent: true,
    }),
  );
  mirror.scale.y = -1;
  mirror.position.y = -PH * 1.04;
  group.add(mirror);
  // Lift the whole group (mark + its mirror reflection) higher on phones, where
  // the tall portrait viewport otherwise lets the reflection collide with the
  // bottom copy block. Desktop has the room, so it stays put.
  group.position.y = isMobile ? 1.5 : 0.55;

  function dustTex() {
    const c = document.createElement('canvas');
    c.width = c.height = 32;
    const g = c.getContext('2d')!;
    const r = g.createRadialGradient(16, 16, 0, 16, 16, 16);
    r.addColorStop(0, 'rgba(255,236,190,1)');
    r.addColorStop(1, 'rgba(255,236,190,0)');
    g.fillStyle = r;
    g.fillRect(0, 0, 32, 32);
    return new THREE.CanvasTexture(c);
  }
  const DUST = isMobile ? 80 : 140;
  const dp = new Float32Array(DUST * 3);
  for (let i = 0; i < DUST; i++) {
    dp[i * 3] = (Math.random() - 0.5) * 24;
    dp[i * 3 + 1] = (Math.random() - 0.5) * 12;
    dp[i * 3 + 2] = -2 - Math.random() * 5;
  }
  const dustGeo = new THREE.BufferGeometry();
  dustGeo.setAttribute('position', new THREE.BufferAttribute(dp, 3));
  const dust = new THREE.Points(
    dustGeo,
    new THREE.PointsMaterial({
      size: 0.07,
      map: dustTex(),
      transparent: true,
      opacity: 0.26,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      color: 0xd8b86a,
    }),
  );
  scene.add(dust);
  const dustBase = dp.slice();

  const composer = new EffectComposer(renderer);
  composer.addPass(new RenderPass(scene, camera));
  composer.addPass(
    new UnrealBloomPass(
      new THREE.Vector2(host.clientWidth, host.clientHeight),
      0.36,
      0.6,
      0.85,
    ),
  );

  /* ---------- choreography & interaction ---------- */
  let T0 = performance.now() / 1000;
  if (reduceMotion) T0 -= 10; // skip straight to the settled mark
  let settledFired = false;
  const ray = new THREE.Raycaster();
  const ndc = new THREE.Vector2();
  let ti = 0;
  let lastT = 0;
  function addTrail(uv: { x: number; y: number }, amp: number): void {
    uniforms.uTrail.value[ti++ % TRAIL]!.set(uv.x, uv.y, 0, amp);
  }
  let rx = 0,
    ry = 0,
    vx = 0,
    vy = 0,
    px = 0,
    py = 0;
  const onMove = (e: PointerEvent): void => {
    px = (e.clientX / innerWidth) * 2 - 1;
    py = (e.clientY / innerHeight) * 2 - 1;
    ndc.set(px, -py);
    ray.setFromCamera(ndc, camera);
    const hit = ray.intersectObject(mesh)[0];
    const now = performance.now();
    if (hit?.uv && now - lastT > (coarse ? 22 : 34)) {
      lastT = now;
      addTrail(hit.uv, coarse ? 0.85 : 0.55);
    }
  };
  const onDown = (e: PointerEvent): void => {
    ndc.set((e.clientX / innerWidth) * 2 - 1, -((e.clientY / innerHeight) * 2 - 1));
    ray.setFromCamera(ndc, camera);
    const hit = ray.intersectObject(mesh)[0];
    if (hit?.uv) addTrail(hit.uv, coarse ? 2.2 : 1.8);
  };
  window.addEventListener('pointermove', onMove);
  window.addEventListener('pointerdown', onDown);

  // Gyroscope parallax: tilting the phone rolls the light across the metal.
  // iOS requires a user-gesture permission request; we ask on first touch.
  let gx = 0,
    gy = 0,
    gyroOn = false;
  const onOrient = (e: DeviceOrientationEvent): void => {
    if (e.beta == null || e.gamma == null) return;
    gyroOn = true;
    gx = Math.max(-1, Math.min(1, e.gamma / 28));
    gy = Math.max(-1, Math.min(1, (e.beta - 50) / 28));
  };
  window.addEventListener('deviceorientation', onOrient);
  const askGyro = (): void => {
    const D = DeviceOrientationEvent as unknown as {
      requestPermission?: () => Promise<string>;
    };
    if (typeof D.requestPermission === 'function') {
      D.requestPermission().catch(() => undefined);
    }
  };
  window.addEventListener('pointerdown', askGyro, { once: true });

  const onResize = (): void => {
    camera.aspect = host.clientWidth / host.clientHeight;
    camera.updateProjectionMatrix();
    renderer.setSize(host.clientWidth, host.clientHeight);
    composer.setSize(host.clientWidth, host.clientHeight);
  };
  window.addEventListener('resize', onResize);

  
  // On touch devices (no hover) the gold gleams on its own: every few
  // seconds a burnish streak sweeps the mark, as if a light passed over it.
  let nextSweep = performance.now() / 1000 + 4.5;
  let sweepStart = -1;
  function autoSweep(now: number): void {
    if (!coarse) return;
    if (sweepStart < 0 && now > nextSweep) {
      sweepStart = now;
      nextSweep = now + 5.5 + Math.random() * 3;
    }
    if (sweepStart >= 0) {
      const sp = (now - sweepStart) / 1.1;
      if (sp >= 1) {
        sweepStart = -1;
        return;
      }
      addTrail({ x: sp, y: 0.5 + Math.sin(sp * Math.PI) * 0.08 }, 0.5);
    }
  }

  let raf = 0;
  function loop(): void {
    raf = requestAnimationFrame(loop);
    const now = performance.now() / 1000;
    const t = now - T0;
    uniforms.uTime.value = now;
    uniforms.uApply.value = Math.min(1.6, t / 2.1);
    if (!settledFired && uniforms.uApply.value > 1.25) {
      settledFired = true;
      
      onSettled?.();
    }
    uniforms.uTrail.value.forEach((r) => (r.z += 0.016));
    autoSweep(now);
    for (let i = 0; i < DUST; i++) {
      dp[i * 3 + 1] = dustBase[i * 3 + 1]! + Math.sin(now * 0.25 + i) * 0.35;
      dp[i * 3] = dustBase[i * 3]! + Math.cos(now * 0.18 + i * 1.7) * 0.25;
    }
    dustGeo.attributes.position!.needsUpdate = true;
    const tiltX = gyroOn ? gy : py;
    const tiltY = gyroOn ? gx : px;
    vx += (tiltX * 0.1 - rx) * 0.05;
    vy += (tiltY * 0.22 - ry) * 0.05;
    vx *= 0.86;
    vy *= 0.86;
    rx += vx;
    ry += vy;
    group.rotation.x = rx * 0.5;
    group.rotation.y = ry + Math.sin(now * 0.4) * 0.03;
    // Fit the mark to the viewport width DETERMINISTICALLY each frame — on
    // portrait phones the horizontal FOV shrinks, so the whole group scales
    // down until the word fits with margin. No lerp: correct on frame one.
    const halfFov = (camera.fov * Math.PI) / 360;
    const visW = 2 * 8.8 * Math.tan(halfFov) * camera.aspect;
    const fit = Math.min(1, (visW * 0.95) / PW);
    group.scale.setScalar(fit);
    const cz = 8.8 + (1 - Math.min(1, t / 3.0)) * 1.2;
    camera.position.z += (cz - camera.position.z) * 0.05;
    camera.lookAt(0, -0.1, 0);
    composer.render();
  }
  loop();

  return {
    regild: () => {
      T0 = performance.now() / 1000;
      settledFired = true; // copy already revealed; don't re-fire
    },
    dispose: () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerdown', onDown);
      window.removeEventListener('deviceorientation', onOrient);
      window.removeEventListener('pointerdown', askGyro);
      window.removeEventListener('resize', onResize);
      renderer.dispose();
      renderer.domElement.remove();
    },
  };
}
